import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSocket } from '../../context/SocketProvider';

const MAX_NOTIFICATIONS = 20;

/**
 * Real-time, in-app notification bell. Backend has no notification-history
 * model (everything is a live socket push), so this only shows what arrives
 * while the tab is open -- it does not persist across reloads.
 */
export default function NotificationBell() {
  const socket = useSocket();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const containerRef = useRef(null);

  const addNotification = useCallback((notification) => {
    setNotifications((prev) => [
      { id: `${Date.now()}-${Math.random()}`, read: false, at: new Date().toISOString(), ...notification },
      ...prev,
    ].slice(0, MAX_NOTIFICATIONS));
  }, []);

  useEffect(() => {
    if (!socket) return;

    const onChatNotification = (payload) => {
      addNotification({
        type: 'chat',
        title: 'New message',
        message: payload.message,
        fromUserId: payload.from,
      });
    };

    const onOrderNotification = (payload) => {
      addNotification({
        type: 'general',
        title: payload.title || 'Notification',
        message: payload.message,
      });
    };

    socket.on('chatNotification', onChatNotification);
    socket.on('orderNotification', onOrderNotification);

    return () => {
      socket.off('chatNotification', onChatNotification);
      socket.off('orderNotification', onOrderNotification);
    };
  }, [socket, addNotification]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const unreadCount = notifications.filter((n) => !n.read).length;

  const handleOpen = () => {
    setOpen((prev) => !prev);
  };

  const handleNotificationClick = (n) => {
    setNotifications((prev) => prev.map((item) => (item.id === n.id ? { ...item, read: true } : item)));
    setOpen(false);
    if (n.type === 'chat' && n.fromUserId) {
      navigate(`/messages/${n.fromUserId}`);
    }
  };

  const markAllRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        onClick={handleOpen}
        className="relative w-9 h-9 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-500 cursor-pointer"
        aria-label="Notifications"
      >
        <i className="ri-notification-3-line text-lg" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 w-2 h-2 bg-rose-500 rounded-full" />
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-11 w-80 max-h-96 overflow-y-auto bg-white rounded-xl border border-slate-100 shadow-lg z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-50">
            <p className="text-sm font-semibold text-slate-800">Notifications</p>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="text-xs text-teal-600 hover:text-teal-700 cursor-pointer">
                Mark all read
              </button>
            )}
          </div>
          {notifications.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-400">
              <i className="ri-notification-off-line text-2xl mb-2 block" />
              Nothing yet
            </div>
          ) : (
            notifications.map((n) => (
              <button
                key={n.id}
                onClick={() => handleNotificationClick(n)}
                className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 cursor-pointer ${
                  n.read ? '' : 'bg-teal-50/50'
                }`}
              >
                <p className="text-xs font-semibold text-slate-800">{n.title}</p>
                <p className="text-xs text-slate-500 truncate mt-0.5">{n.message}</p>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
